import { spawn } from "node:child_process";
import { closeSync, openSync, readFileSync } from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { readBootstrapState } from "./bootstrap-state.js";
import { readStatusBlob } from "./bootstrap-status.js";
import { getFoundryToken } from "./auth.js";
import { LocalCliError } from "./errors.js";
import { colors } from "./output.js";
import {
  findOnboardingProfile, renderCompanyProfile, companyProfileBodyEquals, upsertMemoryIndex,
  DEFAULT_MEMORY_INDEX_HEADER, COMPANY_PROFILE_PATH, renderFounderRecord, founderRecordBodyEquals,
  FOUNDER_RECORD_PATH, type OnboardingBlock,
} from "./onboarding-profile.js";
import { mintInstallationTokenFromPem, readRepoFileViaApp, commitFilesViaApp } from "./github-app-repo.js";
import { getSignedInUserIdentity } from "./founder-identity.js";

export interface GithubAppCreds { appId: string; slug: string; org: string; installationId: string; pemPath: string }

export function readGithubAppCreds(credsPath?: string): GithubAppCreds | null {
  const p = credsPath ?? path.join(os.homedir(), ".m8t-stack", "github-app.json");
  // malformed or absent → null (best-effort seed; `launch` already validated these creds for a real install).
  try { return JSON.parse(readFileSync(p, "utf8")) as GithubAppCreds; } catch { return null; }
}

export interface SeedContext { endpoint: string; brainRepos: string[]; appCreds: GithubAppCreds; subscriptionId?: string }

export async function resolveSeedContext(opts: {
  endpointOverride?: string; brainOverride?: string; credsPath?: string;
  appCredsOverride?: GithubAppCreds; subscriptionId?: string;
}): Promise<SeedContext | null> {
  const appCreds = opts.appCredsOverride ?? readGithubAppCreds(opts.credsPath);
  if (!appCreds) return null; // no brains were deployed
  let endpoint = opts.endpointOverride;
  let subscriptionId = opts.subscriptionId;
  if (!endpoint) {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({ code: "BOOTSTRAP_NO_STATE", message: "No bootstrap state found.", hint: "Run 'm8t bootstrap launch' first, or pass --endpoint." });
    }
    subscriptionId ??= state.subscriptionId;
    const doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
    endpoint = doc.result?.foundryEndpoint;
    if (!endpoint) {
      throw new LocalCliError({ code: "SEED_NO_ENDPOINT", message: "Could not resolve the Foundry endpoint from the install status.", hint: "Pass --endpoint <foundryEndpoint>." });
    }
  }
  const brainRepos = opts.brainOverride
    ? [opts.brainOverride]
    : [`${appCreds.org}/stacey-brain`, `${appCreds.org}/azzy-brain`];
  return { endpoint, brainRepos, appCreds, subscriptionId };
}

export async function applyProfileToBrain(args: {
  block: OnboardingBlock; brainRepo: string; branch: string; appCreds: GithubAppCreds;
  subscriptionId?: string; azIdentity?: { name: string; email: string };
  now?: string; fetchImpl?: typeof fetch;
}): Promise<void> {
  const token = await mintInstallationTokenFromPem({
    appId: args.appCreds.appId,
    privateKeyPem: readFileSync(args.appCreds.pemPath, "utf8"),
    installationId: args.appCreds.installationId,
    fetchImpl: args.fetchImpl,
  });
  const { profileMd, memoryIndexLine: companyLine } = renderCompanyProfile(args.block, args.now);
  const { founderMd, memoryIndexLine: founderLine } = renderFounderRecord(
    args.block, { subscriptionId: args.subscriptionId, azIdentity: args.azIdentity }, args.now);

  const read = (p: string) => readRepoFileViaApp({ token, repo: args.brainRepo, path: p, fetchImpl: args.fetchImpl });
  const existingProfile = await read(COMPANY_PROFILE_PATH);
  const existingFounder = await read(FOUNDER_RECORD_PATH);
  const existingIndex = (await read("memory/MEMORY.md")) ?? DEFAULT_MEMORY_INDEX_HEADER;

  let nextIndex = upsertMemoryIndex(existingIndex, companyLine, COMPANY_PROFILE_PATH);
  nextIndex = upsertMemoryIndex(nextIndex, founderLine, FOUNDER_RECORD_PATH);

  const profileSame = existingProfile != null && companyProfileBodyEquals(existingProfile, profileMd);
  const founderSame = existingFounder != null && founderRecordBodyEquals(existingFounder, founderMd);
  if (profileSame && founderSame && nextIndex === existingIndex) {
    return; // unchanged — true no-op (no commit churn from a regenerated timestamp)
  }

  await commitFilesViaApp({
    token, repo: args.brainRepo, branch: args.branch,
    message: "seed(brain): founder + company profile from onboarding",
    files: [
      { path: COMPANY_PROFILE_PATH, content: profileMd },
      { path: FOUNDER_RECORD_PATH, content: founderMd },
      { path: "memory/MEMORY.md", content: nextIndex },
    ],
    fetchImpl: args.fetchImpl,
  });
}

/** Spawn a detached `m8t bootstrap seed-profile --watch` that survives this process. */
export function spawnDetachedSeedWatch(): void {
  const logPath = path.join(os.homedir(), ".m8t-stack", "seed-profile.log");
  const fd = openSync(logPath, "a");
  try {
    const child = spawn(process.execPath, [process.argv[1] ?? "", "bootstrap", "seed-profile", "--watch"], {
      detached: true, stdio: ["ignore", fd, fd],
    });
    child.unref();
  } finally {
    closeSync(fd);
  }
}

/** Reactive seed at `bootstrap finish`: seed inline if ready; else background-watch; else (skipped) no-op. */
export async function reactiveSeedOnFinish(args: {
  endpoint?: string; stdout: (s: string) => void;
  getFoundryTokenImpl?: () => Promise<string>; spawnWatch?: () => void;
  fetchImpl?: typeof fetch; appCredsOverride?: GithubAppCreds;
  subscriptionId?: string; azIdentity?: { name: string; email: string };
  getSignedInUserIdentityImpl?: () => Promise<{ name: string; email: string }>;
}): Promise<void> {
  const ctx = await resolveSeedContext({ endpointOverride: args.endpoint, appCredsOverride: args.appCredsOverride, subscriptionId: args.subscriptionId });
  if (!ctx) return; // no GitHub App creds → no brains → nothing to seed
  const token = await (args.getFoundryTokenImpl ?? getFoundryToken)();
  const { hadIntake, block } = await findOnboardingProfile({ endpoint: ctx.endpoint, token, fetchImpl: args.fetchImpl });
  if (block) {
    const azIdentity = args.azIdentity ?? await (args.getSignedInUserIdentityImpl ?? getSignedInUserIdentity)();
    for (const brainRepo of ctx.brainRepos) {
      await applyProfileToBrain({ block, brainRepo, branch: "main", appCreds: ctx.appCreds,
        subscriptionId: ctx.subscriptionId, azIdentity, fetchImpl: args.fetchImpl });
    }
    args.stdout(`${colors.success("✓")} Your advisors now know your company + how to reach you (seeded ${ctx.brainRepos.join(", ")}).\n`);
    return;
  }
  if (!hadIntake) return; // founder skipped the questionnaire — nothing to wait for
  (args.spawnWatch ?? spawnDetachedSeedWatch)();
  args.stdout(
    `${colors.dim("ℹ Your advisors will pick up your company profile when you finish the questionnaire (watching in the background).")}\n` +
    `  ${colors.hint("or run:")} m8t bootstrap seed-profile\n`,
  );
}
